import React, { useEffect, useRef, useState } from 'react';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip, Select, MenuItem, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, useMediaQuery } from '@mui/material';
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TextInputCell } from './TextInputCell';
import { NumberInputCell } from './NumberInputCell';
import { NumberDecimalInputCell } from './NumberDecimalInputCell';
import { HEADER_FOOTER_HEIGHT, ROW_HEIGHT } from '../utils/constants';
import { colors, typography } from '../theme';

interface RetailIncome {
  id: string;
  suite: string;
  tenant_name: string;
  square_feet: number;
  rent_start_month: number;
  annual_bumps: number;
  rent_per_square_foot_per_year: number;
  lease_start_month: number;
  lease_end_month?: number;
  rent_type?: string;
  recovery_start_month: number;
}

// const NumberInputCell = ({ params, handleCellChange, field, prefix = '', suffix = '' }: { 
//   params: any, 
//   handleCellChange: any, 
//   field: string,
//   prefix?: string,
//   suffix?: string 
// }) => {
//   const inputRef = useRef<HTMLInputElement>(null);

//   useEffect(() => {
//     if (params.hasFocus && inputRef.current) {
//       inputRef.current.focus();
//       inputRef.current.select();
//     }
//   }, [params.hasFocus]);

//   return (
//     <div style={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
//       {prefix && <span style={{ color: '#888', marginRight: 2, height: '42px'}}>{prefix}</span>}
//       <input
//         ref={inputRef}
//         type="number"
//         min={0}
//         value={params.value ?? ''}
//         onChange={e => handleCellChange(params.id, field, Number(e.target.value))}
//         style={{
//           width: '100%',
//           border: params.hasFocus ? '2px solid #1976d2' : '1px solid #ccc',
//           background: params.hasFocus ? '#e3f2fd' : 'transparent',
//           padding: '10px 6px',
//           outline: 'none',
//           borderRadius: 4,
//           marginTop: 4,
//           fontSize: 14, 
//           textAlign: 'right'
//         }}
//         onFocus={e => e.target.select()}
//       />
//       {suffix && <span style={{ color: '#888', marginLeft: 2, height: '42px' }}>{suffix}</span>}
//     </div>
//   );
// };  


// const TextInputCell = ({ params, handleCellChange, field }: { 
//   params: any, 
//   handleCellChange: any, 
//   field: string,
// }) => {
//   const inputRef = useRef<HTMLInputElement>(null);

//   useEffect(() => {
//     if (params.hasFocus && inputRef.current) {
//       inputRef.current.focus();
//       inputRef.current.select();
//     }
//   }, [params.hasFocus]);

//   return (
//     <div style={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>

//       <input
//         ref={inputRef}
//         type="text"
//         value={params.value ?? ''}
//         onChange={e => handleCellChange(params.id, field, e.target.value)}
//         style={{
//           width: '100%',
//           border: params.hasFocus ? '2px solid #1976d2' : '1px solid #ccc',
//           background: params.hasFocus ? '#e3f2fd' : 'transparent',
//           padding: '10px 6px',
//           outline: 'none',
//           borderRadius: 4,
//           marginTop: 4,
//           fontSize: 14, 
//           textAlign: 'right'
//         }}
//         onFocus={e => e.target.select()}
//       />
//     </div>
//   );
// };  

const RetailIncomeTable: React.FC<{
  retailIncome: RetailIncome[];
  setRetailIncome: React.Dispatch<React.SetStateAction<RetailIncome[]>>;
  modelDetails: any;
  unitsTotalSqFt?: number;
  showIndustrialColumns?: boolean;
}> = ({ retailIncome, setRetailIncome, modelDetails, unitsTotalSqFt, showIndustrialColumns = false }) => {
  const [unitSqFtTotal, setUnitSqFtTotal] = useState<number>(0);
  const spaceType = (modelDetails?.user_model_field_values || []).find((f: any) => {
    const k = String(f.field_key || '');
    return k === 'space_type' || k.trim() === 'space_type';
  })?.value ?? 'Retail';
  const handleCellChange = (id: string, field: string, value: any) => {
    // Force integer for month fields
    const integerMonthFields = new Set(['lease_start_month', 'lease_end_month', 'rent_start_month']);
    const nextValue = integerMonthFields.has(field) ? (Number.isFinite(Number(value)) ? Math.trunc(Number(value)) : 0) : value;
    setRetailIncome(prev => 
      prev.map(row => 
        row.id === id ? { ...row, [field]: nextValue } : row
      )
    );
  };

  // Flex weights to fill 100% width
  const flexByField: Record<string, number> = {
    suite: 0.8,
    tenant_name: 1.2,
    lease_start_month: 0.9,
    square_feet: 0.9,
    rent_start_month: 0.9,
    annual_bumps: 0.9,
    rent_per_square_foot_per_year: 1,
    rent_type: 0.9,
    annual_rent: 1,
    delete: 0.4,
  };

  // Totals for retail SF and broadcasting them
  const totalRetailSquareFeet = retailIncome.reduce((sum, row) => sum + (row.square_feet || 0), 0);

  // Deprecated event wiring removed in favor of props-based totals

  const columns: GridColDef[] = [
    { 
      field: 'suite', 
      headerName: 'Suite', 
      flex: flexByField.suite,
      minWidth: 70,
      editable: false,
      renderCell: (params) => (
        <TextInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="suite" 
        />
      )
    },
    { 
      field: 'tenant_name', 
      headerName: 'Tenant Name', 
      flex: flexByField.tenant_name,
      minWidth: 140,
      editable: false,
      renderCell: (params) => (
        <TextInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="tenant_name" 
        />
      )
    },
    { 
      field: 'lease_start_month', 
      headerName: 'Lease Start', 
      flex: flexByField.lease_start_month,
      minWidth: 100, 
      editable: false, 
      type: 'number',
      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="lease_start_month"
          prefix="Month"
        />
      )
    },
    ...(showIndustrialColumns ? [{
      field: 'lease_end_month',
      headerName: 'Lease End',
      flex: 0.9,
      minWidth: 100,
      editable: false,
      type: 'number',
      renderCell: (params: any) => (
        <NumberInputCell
          params={params}
          handleCellChange={handleCellChange}
          field="lease_end_month"
          prefix="Month"
        />
      )
    }] as GridColDef[] : []),
    { 
      field: 'square_feet', 
      headerName: 'Square Ft', 
      flex: flexByField.square_feet,
      minWidth: 100, 
      editable: false, 
      type: 'number',

      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="square_feet" 
          suffix="sf"
        />
      )
    },
    { 
      field: 'rent_start_month', 
      headerName: 'Rent Start Month', 
      flex: flexByField.rent_start_month,
      minWidth: 140, 
      editable: false, 
      type: 'number',
      // renderCell: (params) => (
      //   <span>{params.value}</span>
      // ),
      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="rent_start_month"
          prefix="Month"
        />
      )
    },
    { 
      field: 'annual_bumps', 
      headerName: 'Annual Bumps (%)', 
      flex: flexByField.annual_bumps,
      minWidth: 130, 
      editable: false, 
      type: 'number',
      // renderCell: (params) => (
      //   <span>{params.value}%</span>
      // ),
      renderCell: (params) => (
        <NumberDecimalInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="annual_bumps" 
          suffix="%"
        />
      )
    },
    { 
      field: 'rent_per_square_foot_per_year', 
      headerName: 'Rent PSF/Year', 
      flex: flexByField.rent_per_square_foot_per_year,
      minWidth: 140, 
      editable: false, 
      type: 'number',
      // renderCell: (params) => (
      //   <span>${params.value?.toFixed(2)}</span>
      // ),
      renderCell: (params) => (
        <NumberDecimalInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="rent_per_square_foot_per_year" 
          prefix="$"
        />
      )
    },
    ...(showIndustrialColumns ? [{
      field: 'rent_type',
      headerName: 'Rent Type',
      flex: flexByField.rent_type,
      minWidth: 120,
      editable: false,
      renderCell: (params: any) => (
        <Select
          size="small"
          fullWidth
          value={params.value ?? 'Gross'}
          onChange={(e) => handleCellChange(params.id as string, 'rent_type', e.target.value)}
          displayEmpty
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            background: 'transparent',
            fontFamily: typography.fontFamily,
            fontSize: 14.5,
            fontWeight: 600,
            border: 'none',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${colors.grey[400]}` },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `2px solid ${colors.blue}` },
            '& .MuiSelect-select': { textAlign: 'left', py: 0.5 },
          }}
        >
          <MenuItem value="Gross" sx={{ fontFamily: typography.fontFamily, fontSize: 14.5 }}>Gross</MenuItem>
          <MenuItem value="NNN" sx={{ fontFamily: typography.fontFamily, fontSize: 14.5 }}>NNN</MenuItem>
        </Select>
      )
    }] as GridColDef[] : []),
    
    // { 
    //   field: 'monthly_rent', 
    //   headerName: 'Monthly Rent', 
    //   width: 150, 
    //   editable: false, 
    //   type: 'number',
    //   cellClassName: 'greyed-out-cell',
    //   renderCell: (params) => {
    //     const row = params.row;
    //     const monthlyRent = (row.rent_per_square_foot_per_year * row.square_feet) / 12;
    //     const formattedMonthlyRent = monthlyRent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    //     return (
    //       <span style={{ color: '#888' }}>${formattedMonthlyRent}</span>
    //     );
    //   },
    // },
    { 
      field: 'annual_rent', 
      headerName: 'Annual Rent', 
      flex: flexByField.annual_rent,
      minWidth: 120, 
      editable: false, 
      type: 'number',
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => {
        const row = params.row;
        const annualRent = row.rent_per_square_foot_per_year * row.square_feet;
        const formattedAnnualRent = annualRent.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return (
          <span style={{ color: colors.grey[600] }}>${formattedAnnualRent}</span>
        );
      },
    },
    // moved to RecoveryIncomeTable
    { 
      field: 'delete', 
      headerName: '', 
      flex: flexByField.delete,
      minWidth: 70, 
      sortable: false,
      renderHeader: () => <span />, 
      renderCell: (params) => (
        <span className="u-row-action">
          <Tooltip title="Delete">
            <span>
              <IconButton
                onClick={() => deleteRow(params.id)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ),
    },
  ];

  const processRowUpdate = (newRow: RetailIncome) => {

    setRetailIncome((prev) =>
      prev.map((row) => (row.id === newRow.id ? newRow : row))
    );
    return newRow;
  };

  const addRow = () => {
    const newRow: RetailIncome = {
      id: String(Date.now()),
      suite: '',
      tenant_name: '',
      square_feet: 0,
      rent_start_month: 0,
      annual_bumps: 1,
      rent_per_square_foot_per_year: 0,
      lease_start_month: 0,
      lease_end_month: 60,
      rent_type: 'Gross',
      recovery_start_month: 0,
    };
    setRetailIncome((prev) => [...prev, newRow]);
  };

  const deleteRow = (id: GridRowId) => {
    try {
      if (typeof window !== 'undefined' && (document as any)?.activeElement) {
        (document as any).activeElement?.blur?.();
      }
    } catch {}
    requestAnimationFrame(() => {
    setRetailIncome((prev) => prev.filter((row) => row.id !== id));
    });
  };

  const CustomFooter = () => {
    // Calculate total monthly and annual rent from rows
    const computedRows = retailIncome.map(row => {
      const monthlyRent = (row.rent_per_square_foot_per_year * row.square_feet) / 12;
      const annualRent = row.rent_per_square_foot_per_year * row.square_feet;
      return { ...row, monthly_rent: monthlyRent, annual_rent: annualRent };
    });
    
    const totalMonthlyRent = computedRows.reduce((sum, row) => sum + (row.monthly_rent || 0), 0);
    const totalAnnualRent = computedRows.reduce((sum, row) => sum + (row.annual_rent || 0), 0);
    const totalSquareFeet = computedRows.reduce((sum, row) => sum + (row.square_feet || 0), 0);
    const grossSqFtField = modelDetails?.user_model_field_values?.find((field: any) => field.field_key == "Gross Square Feet");
    const grossSqFtValue = grossSqFtField ? Number(grossSqFtField.value) : null;
    const combinedSqFt = (((unitsTotalSqFt ?? unitSqFtTotal) || 0) as number) + (totalSquareFeet || 0);
    const exceedsGross = grossSqFtValue !== null && combinedSqFt > grossSqFtValue;

    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.grey[300]}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          fontSize: 14.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" size="small" onClick={addRow} sx={{ minWidth: 200 }}>
            Add Unit
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-end', width: '100%', alignItems: 'center', color: colors.grey[700] }}>
          <Box sx={{ textAlign: 'right' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Total SF:</Box>{' '}
            <Box component="span" sx={exceedsGross ? { color: colors.error, fontWeight: 700 } : {}}>
              {totalSquareFeet.toLocaleString()}
            </Box>
          </Box>
          {grossSqFtValue !== null && (
            <>
              {!showIndustrialColumns && (
                <Box sx={{ textAlign: 'right', color: exceedsGross ? colors.error : colors.grey[700], fontWeight: exceedsGross ? 700 : 500 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Combined SF:</Box> {combinedSqFt.toLocaleString()}
                </Box>
              )}
              <Box sx={{ textAlign: 'right', color: exceedsGross ? colors.error : colors.grey[700], fontWeight: exceedsGross ? 700 : 500 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>Gross SF:</Box> {grossSqFtValue.toLocaleString()}
              </Box>
            </>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Total Monthly Rent:</Box>{' '}
            ${totalMonthlyRent.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Total Annual Rent:</Box> ${totalAnnualRent.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Box>
        </Box>
      </Box>
    );
  };

  // --- Mobile (card) view -------------------------------------------------
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [editId, setEditId] = useState<string | null>(null);
  const editingRow = editId ? retailIncome.find((r) => r.id === editId) ?? null : null;
  const totalAnnualRentMobile = retailIncome.reduce((s, r) => s + (r.rent_per_square_foot_per_year || 0) * (r.square_feet || 0), 0);

  const renderField = (label: string, value: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#6b7280', fontSize: '0.62rem', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  const mobileCards = (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {retailIncome.map((row) => {
          const annualRent = (row.rent_per_square_foot_per_year || 0) * (row.square_feet || 0);
          return (
            <Box key={row.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.suite ? `Suite ${row.suite}` : 'Suite —'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.tenant_name || 'No tenant'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => setEditId(row.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton size="small" color="error" onClick={() => deleteRow(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
                {renderField('Square Ft', Number(row.square_feet || 0).toLocaleString())}
                {renderField('Rent PSF/Yr', `$${Number(row.rent_per_square_foot_per_year || 0).toLocaleString()}`)}
                {renderField('Annual Rent', `$${annualRent.toLocaleString(undefined, { maximumFractionDigits: 0 })}`)}
                {renderField('Lease (mo)', `${row.lease_start_month || 0}–${row.lease_end_month || 0}`)}
                {showIndustrialColumns && renderField('Rent Type', row.rent_type || 'Gross')}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        <Button variant="contained" size="small" onClick={addRow}>Add Unit</Button>
        <Box sx={{ textAlign: 'right', fontSize: 13, color: '#1f2937', ml: 'auto' }}>
          <div><span style={{ fontWeight: 600 }}>Total SF:</span> {totalRetailSquareFeet.toLocaleString()}</div>
          <div><span style={{ fontWeight: 600 }}>Total Annual Rent:</span> ${totalAnnualRentMobile.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </Box>
      </Box>
    </Box>
  );

  const mobileEditDialog = (
    <Dialog open={!!editingRow} onClose={() => setEditId(null)} fullWidth maxWidth="xs">
      <DialogTitle>Edit Suite {editingRow?.suite || ''}</DialogTitle>
      <DialogContent>
        {editingRow && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Suite" size="small" fullWidth value={editingRow.suite ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'suite', e.target.value)} />
            <TextField label="Tenant Name" size="small" fullWidth value={editingRow.tenant_name ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'tenant_name', e.target.value)} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <MobileNumberField label="Lease Start (mo)" type="number" size="small" fullWidth value={editingRow.lease_start_month ?? ''}
                onChange={(e) => handleCellChange(editingRow.id, 'lease_start_month', Number(e.target.value))} />
              <MobileNumberField label="Lease End (mo)" type="number" size="small" fullWidth value={editingRow.lease_end_month ?? ''}
                onChange={(e) => handleCellChange(editingRow.id, 'lease_end_month', Number(e.target.value))} />
            </Box>
            <MobileNumberField label="Square Feet" type="number" size="small" fullWidth value={editingRow.square_feet ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'square_feet', Number(e.target.value))} />
            <MobileNumberField label="Rent Start Month" type="number" size="small" fullWidth value={editingRow.rent_start_month ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'rent_start_month', Number(e.target.value))} />
            <MobileNumberField label="Annual Bumps (%)" type="number" size="small" fullWidth value={editingRow.annual_bumps ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'annual_bumps', Number(e.target.value))} />
            <MobileNumberField label="Rent PSF / Year" type="number" size="small" fullWidth value={editingRow.rent_per_square_foot_per_year ?? ''}
              onChange={(e) => handleCellChange(editingRow.id, 'rent_per_square_foot_per_year', Number(e.target.value))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            {showIndustrialColumns && (
              <TextField select label="Rent Type" size="small" fullWidth value={editingRow.rent_type ?? 'Gross'}
                onChange={(e) => handleCellChange(editingRow.id, 'rent_type', e.target.value)}>
                <MenuItem value="Gross">Gross</MenuItem>
                <MenuItem value="NNN">NNN</MenuItem>
              </TextField>
            )}
            <TextField label="Annual Rent (calculated)" size="small" fullWidth disabled
              value={`$ ${((editingRow.rent_per_square_foot_per_year || 0) * (editingRow.square_feet || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditId(null)} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* <Typography variant="h6" style={{ fontWeight: 'bold' }}>Retail Income</Typography> */}

      </div>
      {isMobile ? mobileCards : (
        <DataGrid
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableColumnSorting
          rows={retailIncome}
          columns={columns}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          slots={{
            footer: CustomFooter,
          }}
          rowHeight={52}
          getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even'}
          sx={{
            minWidth: '1000px',
            background: colors.white,
            border: `1px solid ${colors.grey[300]}`,
            borderRadius: 2,
            fontFamily: typography.fontFamily,
            fontSize: 14.5,
            '& .MuiDataGrid-main': { background: colors.white },
            '& .MuiDataGrid-columnHeaders': { background: colors.white, minHeight: 52, maxHeight: 52, borderBottom: `1px solid ${colors.grey[300]}` },
            '& .MuiDataGrid-columnHeader': { background: colors.white, minHeight: 52, maxHeight: 52 },
            '& .MuiDataGrid-columnHeaderTitleContainer': { background: colors.white },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: 14.5,
              fontFamily: typography.fontFamily,
              textTransform: 'none',
              lineHeight: '52px',
              color: colors.grey[900],
            },
            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[300]}`, background: colors.white, fontSize: 14.5, color: colors.grey[900] },
            '& .greyed-out-cell': { color: colors.grey[600] },
            '& .u-editable-input': { border: 'none', borderBottom: '2px solid transparent', borderRadius: 0, background: 'transparent', fontWeight: 600 },
            '& .u-editable-input input': { fontWeight: 600 },
            '& .MuiDataGrid-row': { background: colors.white },
            '& .u-row-even': { background: colors.grey[50] },
            '& .MuiDataGrid-row:hover': { backgroundColor: colors.blueTint },
            '& .u-row-action': {
              opacity: 0,
              visibility: 'hidden',
              pointerEvents: 'none',
              transition: 'opacity 120ms ease'
            },
            '& .MuiDataGrid-row:hover .u-row-action, & .MuiDataGrid-cell:focus-within .u-row-action': {
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto'
            },
            '& .MuiDataGrid-virtualScroller': { background: colors.white },
            '& .MuiDataGrid-footerContainer': { background: colors.white },
          }}
        />
      )}
      {mobileEditDialog}
    </div>
  );
};

export default RetailIncomeTable;
